/**
 * AI Chat Service — talks to the AI assistant inside the `coreadmin` Lambda
 * through the coreAdmin REST API (POST /coreadmin/ai). The Lambda uses:
 *   - GPT-5.6 for answers and for reading attached screenshots (Bedrock Nova
 *     when no OpenAI key is configured)
 *   - OpenAI speech-to-text for the microphone (Amazon Transcribe as fallback)
 *   - OpenAI text-to-speech for spoken replies (Amazon Polly as fallback)
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiPost } from '../utils/api';

const API_NAME = 'coreAdmin';
const PATH = '/coreadmin/ai';

const callApi = async (body) => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return apiPost(API_NAME, PATH, body, token);
};

/**
 * Send a text conversation to the assistant.
 * A message may carry `images`: base64 data URLs of screenshots or photos the
 * admin attached, which the model reads alongside the question.
 * @param {Array<{role: 'user'|'assistant', text: string, images?: string[]}>} messages
 * @returns {Promise<{reply: string, lang: string}>}
 */
export const sendChat = (messages) => callApi({ action: 'chat', messages });

/**
 * Send a voice recording; server transcribes, answers and speaks in one trip.
 * @param {string} audioBase64 - base64 16kHz 16-bit mono PCM
 * @param {string} lang - 'auto' | 'en' | 'ar' | 'fr'. 'auto' lets the server
 *   detect it, which is what the admin needs when switching language mid-sentence.
 * @param {Array} messages - previous conversation turns
 * @param {boolean} wantAudio - whether to return a spoken (mp3) reply
 * @param {string[]} images - data URLs attached to this spoken question
 * @returns {Promise<{transcript: string, rawTranscript: string, corrections: Array, reply: string|null, lang: string, audio: string|null}>}
 */
export const sendVoice = (audioBase64, lang, messages, wantAudio = true, images = []) =>
  callApi({ action: 'voice', audio: audioBase64, lang, messages, wantAudio, images });

/**
 * Synthesize speech for arbitrary text (Polly neural voice).
 * @returns {Promise<{audio: string}>} base64 mp3
 */
export const synthesizeSpeech = (text, lang) => callApi({ action: 'speak', text, lang });

/**
 * Execute a write-tool the admin just confirmed in the popup
 * (second phase of the confirmation flow).
 * @param {string} tool - tool name from the pendingAction
 * @param {object} input - tool input from the pendingAction
 * @returns {Promise<{tool: string, result: object}>}
 */
export const executeTool = (tool, input) => callApi({ action: 'execute_tool', tool, input });

/**
 * Play base64 audio returned by the backend (AAC from OpenAI, mp3 from Polly).
 * @param {string} base64
 * @param {string} mime - e.g. 'audio/aac' or 'audio/mp3' (default)
 * @returns {HTMLAudioElement} the playing element (call .pause() to stop)
 */
export const playBase64Audio = (base64, mime = 'audio/mp3') => {
  const audio = new Audio(`data:${mime};base64,${base64}`);
  audio.play().catch((e) => console.warn('Audio playback blocked:', e));
  return audio;
};

/* ============================== Attachments ============================== */

/** How many screenshots/photos can ride along with one question. */
export const MAX_ATTACHMENTS = 4;

/**
 * Longest edge of an attachment after downscaling.
 *
 * A phone photo or a retina screenshot is several thousand pixels wide, which
 * costs upload time and image tokens for detail nobody reads. 1600px keeps
 * panel text and bet slips legible while staying well inside the API Gateway
 * request limit.
 */
const MAX_IMAGE_EDGE = 1600;
const JPEG_QUALITY = 0.85;

export const isImageFile = (file) => !!file && typeof file.type === 'string' && file.type.startsWith('image/');

const loadImage = (dataUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('That file could not be read as an image'));
  image.src = dataUrl;
});

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('That file could not be read'));
  reader.readAsDataURL(file);
});

/**
 * Turn a picked, pasted or dropped file into an attachment the API accepts:
 * a downscaled base64 JPEG plus the metadata the thumbnail needs.
 *
 * PNG screenshots are re-encoded as JPEG because a full-screen PNG is often
 * 5-10x larger for no readability gain. Animated GIFs are passed through
 * untouched (the canvas would flatten them to one frame anyway).
 *
 * @returns {Promise<{dataUrl: string, name: string, width: number, height: number, bytes: number}>}
 */
export async function prepareAttachment(file) {
  if (!isImageFile(file)) {
    throw new Error('Only images can be attached');
  }

  const original = await readAsDataUrl(file);
  const name = file.name || 'screenshot';

  if (file.type === 'image/gif') {
    return { dataUrl: original, name, width: 0, height: 0, bytes: file.size || 0 };
  }

  const image = await loadImage(original);
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  // Screenshots are usually pasted on a light panel; a white backdrop keeps
  // transparent PNG areas readable instead of turning them black in JPEG.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return { dataUrl, name, width, height, bytes: Math.round(dataUrl.length * 0.75) };
}

/** Prepare several files at once, skipping the ones that cannot be read. */
export async function prepareAttachments(files) {
  const images = Array.from(files || []).filter(isImageFile).slice(0, MAX_ATTACHMENTS);
  const results = await Promise.allSettled(images.map(prepareAttachment));
  for (const result of results) {
    if (result.status === 'rejected') console.warn('Attachment skipped:', result.reason);
  }
  return results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
}

/** Image files carried by a paste or drop event, if any. */
export function imagesFromDataTransfer(dataTransfer) {
  if (!dataTransfer) return [];
  const items = Array.from(dataTransfer.items || []);
  const fromItems = items
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter(Boolean);
  return fromItems.length > 0 ? fromItems : Array.from(dataTransfer.files || []).filter(isImageFile);
}

/** Longest single recording, in seconds. */
export const MAX_RECORD_SECONDS = 60;

// Frame loudness (RMS) above which we consider someone to be talking, and
// below which we start counting silence. The gap between the two is
// hysteresis, so a brief dip mid-sentence does not end the recording.
const SPEECH_LEVEL = 0.02;
const SILENCE_LEVEL = 0.012;

/**
 * PCM microphone recorder with level metering and silence detection.
 *
 * Amazon Transcribe wants raw PCM (16 kHz, 16-bit, mono) — MediaRecorder's
 * webm/opus output is not accepted — so we capture with the Web Audio API and
 * downsample manually.
 *
 * `onLevel` fires per audio frame with a 0..1 loudness, which drives the live
 * waveform. When `autoStop` is on, the recording ends by itself once the admin
 * has stopped talking for `silenceMs`, so there is no button to hunt for.
 */
export class PcmRecorder {
  constructor({
    targetSampleRate = 16000,
    maxSeconds = MAX_RECORD_SECONDS,
    onStop = null,
    onLevel = null,
    autoStop = true,
    silenceMs = 1500
  } = {}) {
    this.targetSampleRate = targetSampleRate;
    this.maxSeconds = maxSeconds;
    this.onStop = onStop;
    this.onLevel = onLevel;
    this.autoStop = autoStop;
    this.silenceMs = silenceMs;
    this.chunks = [];
    this.recording = false;
    this.heardSpeech = false;
    this.silenceStartedAt = null;
  }

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    });
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioCtx();
    this.sourceSampleRate = this.audioContext.sampleRate;
    this.source = this.audioContext.createMediaStreamSource(this.stream);
    // ScriptProcessorNode is deprecated but universally supported and fine
    // for short one-shot recordings; AudioWorklet would need a separate file.
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    // Muted sink: the processor only runs while connected, but routing the mic
    // to the speakers would echo.
    this.silentGain = this.audioContext.createGain();
    this.silentGain.gain.value = 0;

    this.chunks = [];
    this.recording = true;
    this.heardSpeech = false;
    this.silenceStartedAt = null;

    this.processor.onaudioprocess = (event) => {
      if (!this.recording) return;
      const frame = event.inputBuffer.getChannelData(0);
      this.chunks.push(new Float32Array(frame));

      let sum = 0;
      for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
      const rms = Math.sqrt(sum / frame.length);

      if (this.onLevel) {
        // Speech rarely exceeds ~0.25 RMS, so scale that to a full meter
        this.onLevel(Math.min(1, rms / 0.25));
      }

      if (rms >= SPEECH_LEVEL) {
        this.heardSpeech = true;
        this.silenceStartedAt = null;
      } else if (this.heardSpeech && rms < SILENCE_LEVEL) {
        const now = Date.now();
        if (this.silenceStartedAt === null) {
          this.silenceStartedAt = now;
        } else if (this.autoStop && now - this.silenceStartedAt >= this.silenceMs) {
          this.finish();
        }
      }
    };

    this.source.connect(this.processor);
    this.processor.connect(this.silentGain);
    this.silentGain.connect(this.audioContext.destination);

    this.maxTimer = setTimeout(() => this.finish(), this.maxSeconds * 1000);
  }

  /** Stop and hand the recording to the onStop callback. */
  finish() {
    if (!this.recording) return;
    const result = this.stop();
    if (this.onStop) this.onStop(result);
  }

  /** Release the microphone and audio graph. */
  teardown() {
    this.recording = false;
    clearTimeout(this.maxTimer);
    try {
      this.source?.disconnect();
      this.processor?.disconnect();
      this.silentGain?.disconnect();
      this.stream?.getTracks().forEach((t) => t.stop());
      this.audioContext?.close();
    } catch (e) {
      console.warn('Recorder cleanup error:', e);
    }
    if (this.processor) this.processor.onaudioprocess = null;
  }

  /**
   * Stop recording and return base64-encoded 16 kHz 16-bit mono PCM.
   * @returns {{base64: string, durationSec: number, heardSpeech: boolean}}
   */
  stop() {
    const heardSpeech = this.heardSpeech;
    this.teardown();

    const totalLength = this.chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of this.chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    this.chunks = [];

    const downsampled = downsample(merged, this.sourceSampleRate, this.targetSampleRate);
    const pcm16 = floatTo16BitPcm(downsampled);
    return {
      base64: arrayBufferToBase64(pcm16.buffer),
      durationSec: totalLength / this.sourceSampleRate,
      heardSpeech
    };
  }

  cancel() {
    this.teardown();
    this.chunks = [];
  }
}

const downsample = (samples, fromRate, toRate) => {
  if (toRate >= fromRate) return samples;
  const ratio = fromRate / toRate;
  const newLength = Math.round(samples.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), samples.length);
    let sum = 0;
    for (let j = start; j < end; j++) sum += samples[j];
    result[i] = end > start ? sum / (end - start) : 0;
  }
  return result;
};

const floatTo16BitPcm = (float32) => {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};
